import React, { useState, useRef, useEffect } from 'react';
import './LazyYouTube.css';

/**
 * LazyYouTube – Shows a YouTube thumbnail until clicked or scrolled into view.
 * Dramatically reduces page load time by avoiding heavy iframe embeds.
 *
 * Props:
 *   videoId   – YouTube video ID (e.g. "6rYLlwK-oxg")
 *   title     – accessible title for the iframe
 *   className – extra class on the wrapper
 *   style     – inline style on the wrapper
 */
const LazyYouTube = ({ videoId, title = 'YouTube video', className = '', style }) => {
  const [activated, setActivated] = useState(false);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  // Intersection Observer – mark as "in view" when near viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // start loading 200px before visible
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const thumbUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <div
      ref={ref}
      className={`lazy-yt ${className}`}
      style={style}
      onClick={() => setActivated(true)}
      role="button"
      tabIndex={0}
      aria-label={`Play: ${title}`}
      onKeyDown={(e) => { if (e.key === 'Enter') setActivated(true); }}
    >
      {activated && inView ? (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          {inView && <img src={thumbUrl} alt={title} loading="lazy" />}
          <div className="lazy-yt-play" aria-hidden="true">
            <svg viewBox="0 0 68 48" width="68" height="48">
              <path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.63-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="#212121" fillOpacity="0.8"/>
              <path d="M45 24L27 14v20" fill="#fff"/>
            </svg>
          </div>
        </>
      )}
    </div>
  );
};

export default LazyYouTube;
